import React from 'react';
import dayjs from 'dayjs';
import { Button, Input, Select, Space, Switch, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { CompletionProjection, WorkspaceTask } from '../../store/api/workspaceApi';
import {
  WORKSPACE_GRID_CELL,
  WORKSPACE_GRID_ROW,
  WORKSPACE_ROUTE_GRID_ROW,
  WORKSPACE_APPLICATION_GRID_ROW,
  WORKSPACE_APPLICATION_REGISTRY_GRID_ROW,
} from './workspaceGrid';

/** Sticky-заголовок колонок (тот же grid, что у строк) */
export const WORKSPACE_HEADER_ROW: React.CSSProperties = {
  ...WORKSPACE_GRID_ROW,
  position: 'sticky',
  top: 0,
  zIndex: 4,
  minHeight: 32,
  padding: '5px 10px',
  background: 'var(--ant-color-bg-container, #ffffff)',
  borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
  boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
};

/** Sticky-заголовок для таблиц маршрутов (7 колонок). */
export const WORKSPACE_ROUTE_HEADER_ROW: React.CSSProperties = {
  ...WORKSPACE_ROUTE_GRID_ROW,
  position: 'sticky',
  top: 0,
  zIndex: 4,
  minHeight: 32,
  padding: '5px 10px',
  background: 'var(--ant-color-bg-container, #ffffff)',
  borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
  boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
};

/** Sticky-заголовок для списка заявок в workspace (сетка 6 колонок). */
export const WORKSPACE_APPLICATION_HEADER_ROW: React.CSSProperties = {
  ...WORKSPACE_APPLICATION_GRID_ROW,
  position: 'sticky',
  top: 0,
  zIndex: 4,
  minHeight: 32,
  padding: '5px 10px',
  background: 'var(--ant-color-bg-container, #ffffff)',
  borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
  boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
};

/** Sticky-заголовок реестра заявок (7 колонок, + статус оплаты). */
export const WORKSPACE_APPLICATION_REGISTRY_HEADER_ROW: React.CSSProperties = {
  ...WORKSPACE_APPLICATION_REGISTRY_GRID_ROW,
  position: 'sticky',
  top: 0,
  zIndex: 4,
  minHeight: 32,
  padding: '5px 10px',
  background: 'var(--ant-color-bg-container, #ffffff)',
  borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
  boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
};

export type WorkspaceStatusFilter = 'all' | 'in_work' | 'overdue' | 'completed';

function projectionCompleted(projection?: CompletionProjection | null): boolean {
  if (!projection) return false;
  return Boolean(projection.overallComplete);
}

function projectionActive(projection?: CompletionProjection | null): boolean {
  if (!projection) return true;
  return !projection.overallComplete;
}

export function matchesApplicationStatusFilter(
  status: string,
  filter: WorkspaceStatusFilter,
  overdueHighlight: boolean,
  completionProjection?: CompletionProjection | null
): boolean {
  const completed = projectionCompleted(completionProjection);
  if (filter === 'all') return true;
  if (filter === 'completed') return completed;
  if (filter === 'overdue') return overdueHighlight;
  if (filter === 'in_work') return projectionActive(completionProjection);
  return true;
}

export function matchesRouteStatusFilter(
  status: string,
  filter: WorkspaceStatusFilter,
  overdueHighlight: boolean,
  completionProjection?: CompletionProjection | null
): boolean {
  const completed = projectionCompleted(completionProjection);
  if (filter === 'all') return true;
  if (filter === 'completed') return completed;
  if (filter === 'overdue') return overdueHighlight;
  if (filter === 'in_work') return projectionActive(completionProjection);
  return true;
}
export type WorkspaceScopeFilter = 'mine' | 'all';
export type WorkspaceHasTasksFilter = 'all' | 'with' | 'without';
export type WorkspaceSortKey = 'date' | 'tasks' | 'status';

type HeaderCellProps = {
  children: React.ReactNode;
  sortKey?: WorkspaceSortKey;
  activeKey: WorkspaceSortKey | null;
  activeDir: 'asc' | 'desc';
  onSort: (key: WorkspaceSortKey) => void;
};

export function WorkspaceHeaderCell({ children, sortKey, activeKey, activeDir, onSort }: HeaderCellProps) {
  const active = sortKey != null && activeKey === sortKey;
  const style: React.CSSProperties = {
    ...WORKSPACE_GRID_CELL,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    justifyContent: 'flex-start',
    cursor: sortKey != null ? 'pointer' : 'default',
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    fontWeight: 600,
    color: 'inherit',
    textAlign: 'left',
    padding: 0,
  };

  if (sortKey == null) {
    return (
      <div style={style}>
        <span>{children}</span>
      </div>
    );
  }

  return (
    <button type="button" onClick={() => onSort(sortKey)} style={style}>
      <span>{children}</span>
      {active && <span aria-hidden>{activeDir === 'asc' ? '↑' : '↓'}</span>}
    </button>
  );
}

export type WorkspaceToolbarProps = {
  searchPlaceholder: string;
  search: string;
  onSearch: (v: string) => void;
  status: WorkspaceStatusFilter;
  onStatus: (v: WorkspaceStatusFilter) => void;
  scope: WorkspaceScopeFilter;
  onScope: (v: WorkspaceScopeFilter) => void;
  hasTasks: WorkspaceHasTasksFilter;
  onHasTasks: (v: WorkspaceHasTasksFilter) => void;
  /** «Мои / все» — только для списков заявок/маршрутов (не для блоков «уже с задачами»). */
  showScopeFilter?: boolean;
  /** Фильтр «есть / нет задач» — в блоках, где у каждой строки уже есть задачи, можно скрыть. */
  showHasTasksFilter?: boolean;
  showCompleted?: boolean;
  onShowCompleted?: (v: boolean) => void;
  onQuickFilter?: (preset: 'problems' | 'overdue' | 'active' | 'without_tasks' | 'awaiting_docs') => void;
  activeFilters?: string[];
  onResetFilters?: () => void;
};

export function WorkspaceListToolbar({
  searchPlaceholder,
  search,
  onSearch,
  status,
  onStatus,
  scope,
  onScope,
  hasTasks,
  onHasTasks,
  showScopeFilter = true,
  showHasTasksFilter = true,
  showCompleted,
  onShowCompleted,
  onQuickFilter,
  activeFilters = [],
  onResetFilters,
}: WorkspaceToolbarProps) {
  return (
    <div style={{ marginBottom: 6 }}>
      <Space wrap size={[6, 6]} style={{ width: '100%', marginBottom: 6 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          style={{ minWidth: 160, maxWidth: 280, flex: 1 }}
        />
        <Select<WorkspaceStatusFilter>
          value={status}
          onChange={onStatus}
          style={{ width: 150 }}
          options={[
            { value: 'all', label: 'Статус: все' },
            { value: 'in_work', label: 'В работе' },
            { value: 'overdue', label: 'Просрочка' },
            { value: 'completed', label: 'Завершено' },
          ]}
        />
        {showHasTasksFilter ? (
          <Select<WorkspaceHasTasksFilter>
            value={hasTasks}
            onChange={onHasTasks}
            style={{ width: 150 }}
            options={[
              { value: 'all', label: 'Задачи: любые' },
              { value: 'with', label: 'Есть задачи' },
              { value: 'without', label: 'Нет задач' },
            ]}
          />
        ) : null}
        {showScopeFilter ? (
          <Select<WorkspaceScopeFilter>
            value={scope}
            onChange={onScope}
            style={{ width: 120 }}
            options={[
              { value: 'mine', label: 'Мои' },
              { value: 'all', label: 'Все' },
            ]}
          />
        ) : null}
        {typeof showCompleted === 'boolean' && onShowCompleted ? (
          <Space size={6}>
            <Switch checked={showCompleted} onChange={onShowCompleted} />
            <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>Показать завершенные</span>
          </Space>
        ) : null}
      </Space>
      <Space wrap size={[8, 8]} style={{ width: '100%', marginBottom: activeFilters.length > 0 ? 6 : 0 }}>
        <Button size="small" onClick={() => onQuickFilter?.('problems')}>
          Только проблемы
        </Button>
        <Button size="small" onClick={() => onQuickFilter?.('overdue')}>
          Только просрочки
        </Button>
        <Button size="small" onClick={() => onQuickFilter?.('active')}>
          Мои активные
        </Button>
        <Button size="small" onClick={() => onQuickFilter?.('without_tasks')}>
          Без задач
        </Button>
        <Button size="small" onClick={() => onQuickFilter?.('awaiting_docs')}>
          Ожидают документы
        </Button>
        {onResetFilters ? (
          <Button size="small" onClick={onResetFilters}>
            Сбросить фильтры
          </Button>
        ) : null}
      </Space>
      {activeFilters.length > 0 && (
        <Space wrap size={[6, 6]}>
          <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>Активные фильтры:</span>
          {activeFilters.map((item) => (
            <Tag key={item}>{item}</Tag>
          ))}
        </Space>
      )}
    </div>
  );
}

/** Подсветка строки: просрочка → красный, сегодня → жёлтый */
export function workspaceRowHighlightStyle(overdue: boolean, today: boolean): React.CSSProperties {
  if (overdue) {
    return {
      border: '1px solid #ffccc7',
      background: '#fff2f0',
    };
  }
  if (today) {
    return {
      border: '1px solid #ffe58f',
      background: '#fffbe6',
    };
  }
  return {
    border: '1px solid #e8e8e8',
    background: '#fafafa',
  };
}

function taskIsOverdue(t: WorkspaceTask): boolean {
  return String(t.status || '').toUpperCase() === 'OVERDUE';
}

function taskIsDueTodayNotOverdue(t: WorkspaceTask): boolean {
  if (taskIsOverdue(t)) return false;
  if (!t.dueDate) return false;
  const d = dayjs(t.dueDate);
  return d.isValid() && d.isSame(dayjs(), 'day');
}

/** Подсветка строки списка по активным задачам workspace для заявки или маршрута. */
export function workspaceHighlightForApplication(
  applicationId: number,
  tasks: WorkspaceTask[]
): { overdue: boolean; today: boolean } {
  const rel = tasks.filter((t) => t.applicationId === applicationId);
  let overdue = false;
  let today = false;
  for (const t of rel) {
    if (taskIsOverdue(t)) overdue = true;
    else if (taskIsDueTodayNotOverdue(t)) today = true;
  }
  return { overdue, today: overdue ? false : today };
}

export function workspaceHighlightForRoute(routeId: number, tasks: WorkspaceTask[]): { overdue: boolean; today: boolean } {
  const rel = tasks.filter((t) => t.routeId === routeId);
  let overdue = false;
  let today = false;
  for (const t of rel) {
    if (taskIsOverdue(t)) overdue = true;
    else if (taskIsDueTodayNotOverdue(t)) today = true;
  }
  return { overdue, today: overdue ? false : today };
}

export function toggleSort(
  key: WorkspaceSortKey,
  current: WorkspaceSortKey | null,
  dir: 'asc' | 'desc'
): { key: WorkspaceSortKey | null; dir: 'asc' | 'desc' } {
  if (current !== key) {
    return { key, dir: 'asc' };
  }
  if (dir === 'asc') {
    return { key, dir: 'desc' };
  }
  return { key: null, dir: 'asc' };
}
